import { safeAuthRedirect } from '../utils/authRedirect';
import React, { useState, useEffect } from 'react';
import { Navigate, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useParadigm } from '../contexts/ParadigmContext';
import axios from 'axios';
import GoogleSSOButton from './GoogleSSOButton';
import MicrosoftSSOButton from './MicrosoftSSOButton';
import SAMLSSOButton from './SAMLSSOButton';
import OIDCSSOButton from './OIDCSSOButton';
import { getApiBase } from '../utils/api';
import {
  isWebAuthnSupported,
  authenticateWithPasskey,
} from '../utils/webauthn';

const SAVED_EMAIL_KEY = 'veyage_saved_login_email';

const Login = () => {
  const { labels } = useParadigm();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [step, setStep] = useState('email'); // 'email' or 'auth'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    organization_id: '',
    role: 'ea'
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [isPasskeySupported, setIsPasskeySupported] = useState(false);
  const [savedEmail, setSavedEmail] = useState('');
  const [rememberEmail, setRememberEmail] = useState(true);
  const [showDifferentEmailInput, setShowDifferentEmailInput] = useState(false);
  // Email one-time code (sign-in code / forgot password) state
  const [otpMode, setOtpMode] = useState(null); // null | 'login' | 'reset'
  const [otpCode, setOtpCode] = useState('');
  const [otpNewPassword, setOtpNewPassword] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpNotice, setOtpNotice] = useState('');
  const { user, login, register, loginWithPasskey, loginWithEmailCode } = useAuth();

  // Auth methods state based on email check
  const [authMethods, setAuthMethods] = useState({
    saml_available: false,
    saml_required: false,
    oidc_available: false,
    oidc_required: false,
    allow_google_sso: true,
    allow_microsoft_sso: true,
    allow_password_login: true,
    allow_passkey_login: true,
    allow_saml_login: true,
    organization_name: null,
    idp_name: null
  });

  useEffect(() => {
    // Check if WebAuthn is supported
    setIsPasskeySupported(isWebAuthnSupported());

    // Load saved email from localStorage
    const storedEmail = localStorage.getItem(SAVED_EMAIL_KEY);
    if (storedEmail) {
      setSavedEmail(storedEmail);
      setFormData(prev => ({ ...prev, email: storedEmail }));
    }

    // Check for error in URL (e.g., from SAML callback)
    const errorParam = searchParams.get('error');
    if (errorParam) {
      setError(decodeURIComponent(errorParam));
      // Clear the error param from URL to avoid showing it on refresh
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }

    // Store returnUrl in sessionStorage so it survives SSO redirects
    const returnUrl = searchParams.get('returnUrl');
    if (returnUrl) {
      sessionStorage.setItem('authReturnUrl', safeAuthRedirect(returnUrl));
    }
  }, [searchParams, setSearchParams]);

  if (user) {
    // Check for returnUrl - first in sessionStorage (set earlier), then in query params
    const storedReturnUrl = sessionStorage.getItem('authReturnUrl');
    const redirectTo = safeAuthRedirect(storedReturnUrl);
    if (storedReturnUrl) {
      sessionStorage.removeItem('authReturnUrl');
    }
    return <Navigate to={redirectTo} replace />;
  }

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleEmailContinue = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    // Save or clear email based on remember preference (before API call)
    if (rememberEmail) {
      try {
        localStorage.setItem(SAVED_EMAIL_KEY, formData.email);
        setSavedEmail(formData.email);
      } catch (storageErr) {
        console.warn('Could not save email to localStorage:', storageErr);
      }
    } else {
      // If user unchecked remember, clear any previously saved email
      try {
        localStorage.removeItem(SAVED_EMAIL_KEY);
        setSavedEmail('');
      } catch (storageErr) {
        console.warn('Could not clear email from localStorage:', storageErr);
      }
    }

    setError('');
    setCheckingEmail(true);

    try {
      const apiBase = getApiBase();
      const response = await axios.get(`${apiBase}/auth/methods`, {
        params: { email: formData.email }
      });

      setAuthMethods(response.data);
      setStep('auth');
    } catch (err) {
      console.error('Error checking auth methods:', err);
      // On error, show all methods as fallback
      setAuthMethods({
        saml_available: false,
        saml_required: false,
        oidc_available: false,
        oidc_required: false,
        allow_google_sso: true,
        allow_microsoft_sso: true,
        allow_password_login: true,
        allow_passkey_login: true,
        allow_saml_login: true,
        organization_name: null,
        idp_name: null
      });
      setStep('auth');
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setError('');
    setShowDifferentEmailInput(false);
  };

  const handleClearSavedEmail = () => {
    localStorage.removeItem(SAVED_EMAIL_KEY);
    setSavedEmail('');
    setFormData(prev => ({ ...prev, email: '' }));
    setShowDifferentEmailInput(false);
  };

  const handleUseDifferentEmail = () => {
    setShowDifferentEmailInput(true);
    setFormData(prev => ({ ...prev, email: '' }));
  };

  const handleUseSavedEmail = () => {
    setShowDifferentEmailInput(false);
    setFormData(prev => ({ ...prev, email: savedEmail }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let result;
      if (isLogin) {
        result = await login(formData.email, formData.password);
      } else {
        result = await register(formData);
      }

      if (!result.success) {
        setError(result.error);
      } else {
        // Successful login - redirect to returnUrl or home
        const storedReturnUrl = sessionStorage.getItem('authReturnUrl');
        sessionStorage.removeItem('authReturnUrl');
        navigate(safeAuthRedirect(storedReturnUrl), { replace: true });
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Request an emailed one-time code (sign-in or password reset)
  const startOtpFlow = async (purpose) => {
    setError('');
    setOtpCode('');
    setOtpNewPassword('');
    setOtpLoading(true);
    try {
      const apiBase = getApiBase();
      await axios.post(`${apiBase}/auth/otp/request`, {
        email: formData.email,
        purpose
      });
      setOtpMode(purpose);
      setOtpNotice(`If an account exists for ${formData.email}, we've emailed a 6-digit code. Enter it below.`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not send the code. Please try again shortly.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setOtpLoading(true);
    try {
      if (otpMode === 'login') {
        const result = await loginWithEmailCode(formData.email, otpCode);
        if (!result.success) {
          setError(result.error);
        } else {
          const storedReturnUrl = sessionStorage.getItem('authReturnUrl');
          sessionStorage.removeItem('authReturnUrl');
          navigate(safeAuthRedirect(storedReturnUrl), { replace: true });
        }
      } else {
        // Password reset: set the new password, then return to sign-in
        const apiBase = getApiBase();
        await axios.post(`${apiBase}/auth/password-reset/confirm`, {
          email: formData.email,
          code: otpCode,
          new_password: otpNewPassword
        });
        setOtpMode(null);
        setOtpNotice('');
        setError('');
        setFormData(prev => ({ ...prev, password: '' }));
        setOtpCode('');
        setOtpNewPassword('');
        setOtpNotice('Password updated — sign in with your new password.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired code');
    } finally {
      setOtpLoading(false);
    }
  };

  const cancelOtpFlow = () => {
    setOtpMode(null);
    setOtpCode('');
    setOtpNewPassword('');
    setOtpNotice('');
    setError('');
  };

  const handlePasskeyLogin = async () => {
    setError('');
    setPasskeyLoading(true);

    try {
      // Pass email if provided, otherwise null for usernameless flow
      const email = formData.email && formData.email.includes('@') ? formData.email : null;
      const authResult = await authenticateWithPasskey(email);

      // Call the loginWithPasskey function from AuthContext to set user state
      await loginWithPasskey(authResult);

      // Successful login - redirect to returnUrl or home
      const storedReturnUrl = sessionStorage.getItem('authReturnUrl');
      sessionStorage.removeItem('authReturnUrl');
      navigate(safeAuthRedirect(storedReturnUrl), { replace: true });
    } catch (err) {
      console.error('Passkey authentication error:', err);
      if (err.name === 'NotAllowedError') {
        setError('Authentication cancelled or timed out');
      } else if (err.name === 'InvalidStateError') {
        setError('No passkey found for this device');
      } else {
        setError(err.message || 'Failed to authenticate with passkey');
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  // Render email input step
  const renderEmailStep = () => (
    <form className="mt-8 space-y-6" onSubmit={handleEmailContinue}>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="space-y-4">
        {/* Show saved email option if available and not showing different email input */}
        {savedEmail && !showDifferentEmailInput ? (
          <div className="space-y-3">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">Continue as</p>
              <p className="font-medium text-gray-900">{savedEmail}</p>
            </div>
            <button
              type="submit"
              disabled={checkingEmail}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {checkingEmail ? 'Checking...' : 'Continue'}
            </button>
            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={handleUseDifferentEmail}
                className="text-indigo-600 hover:text-indigo-500"
              >
                Use a different email
              </button>
              <button
                type="button"
                onClick={handleClearSavedEmail}
                className="text-gray-500 hover:text-gray-700"
              >
                Forget this email
              </button>
            </div>
          </div>
        ) : (
          <>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="you@company.com"
                autoFocus
              />
            </div>

            {/* Remember email checkbox */}
            <div className="flex items-center">
              <input
                id="remember-email"
                name="remember-email"
                type="checkbox"
                checked={rememberEmail}
                onChange={(e) => setRememberEmail(e.target.checked)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="remember-email" className="ml-2 block text-sm text-gray-700">
                Remember this email
              </label>
            </div>

            <button
              type="submit"
              disabled={checkingEmail}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {checkingEmail ? 'Checking...' : 'Continue'}
            </button>

            {/* Show option to go back to saved email if available */}
            {savedEmail && showDifferentEmailInput && (
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleUseSavedEmail}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Use saved email ({savedEmail})
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <div className="text-center">
        <button
          type="button"
          onClick={() => setIsLogin(!isLogin)}
          className="text-indigo-600 hover:text-indigo-500 text-sm"
        >
          {isLogin ? 'Need an account? Sign up' : 'Already have an account? Sign in'}
        </button>
      </div>
    </form>
  );

  // Render emailed one-time code step (sign-in code or password reset)
  const renderOtpStep = () => (
    <form className="mt-8 space-y-6" onSubmit={handleOtpSubmit}>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {otpNotice && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded text-sm">
          {otpNotice}
        </div>
      )}

      <div className="flex items-center justify-between bg-gray-50 px-4 py-3 rounded-lg">
        <div>
          <p className="text-sm text-gray-500">{otpMode === 'reset' ? 'Resetting password for' : 'Signing in as'}</p>
          <p className="font-medium text-gray-900">{formData.email}</p>
        </div>
        <button type="button" onClick={cancelOtpFlow} className="text-sm text-indigo-600 hover:text-indigo-500">
          Back
        </button>
      </div>

      <div>
        <label htmlFor="otp-code" className="block text-sm font-medium text-gray-700 mb-1">
          6-digit code
        </label>
        <input
          id="otp-code"
          name="otp-code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          maxLength={6}
          required
          value={otpCode}
          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
          className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-center text-2xl tracking-[0.5em] font-mono"
          placeholder="••••••"
          autoFocus
        />
      </div>

      {otpMode === 'reset' && (
        <div>
          <label htmlFor="otp-new-password" className="block text-sm font-medium text-gray-700 mb-1">
            New password
          </label>
          <input
            id="otp-new-password"
            name="otp-new-password"
            type="password"
            autoComplete="new-password"
            minLength={8}
            required
            value={otpNewPassword}
            onChange={(e) => setOtpNewPassword(e.target.value)}
            className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="At least 8 characters"
          />
        </div>
      )}

      <button
        type="submit"
        disabled={otpLoading || otpCode.length !== 6}
        className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
      >
        {otpLoading
          ? 'Verifying...'
          : otpMode === 'reset' ? 'Set new password' : 'Sign in'}
      </button>

      <div className="text-center">
        <button
          type="button"
          onClick={() => startOtpFlow(otpMode)}
          disabled={otpLoading}
          className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
        >
          Didn't get it? Send a new code
        </button>
      </div>
    </form>
  );

  // Render authentication methods step
  const renderAuthStep = () => (
    <div className="mt-8 space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {otpNotice && !otpMode && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded text-sm">
          {otpNotice}
        </div>
      )}

      {/* Email display with change option */}
      <div className="flex items-center justify-between bg-gray-50 px-4 py-3 rounded-lg">
        <div>
          <p className="text-sm text-gray-500">Signing in as</p>
          <p className="font-medium text-gray-900">{formData.email}</p>
          {authMethods.organization_name && (
            <p className="text-xs text-indigo-600 mt-1">
              {authMethods.organization_name}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={handleBackToEmail}
          className="text-sm text-indigo-600 hover:text-indigo-500"
        >
          Change
        </button>
      </div>

      {/* OIDC SSO - Primary method when available */}
      {authMethods.oidc_available && (
        <div className="space-y-3">
          <OIDCSSOButton
            email={formData.email}
            onError={(err) => setError(err)}
            idpName={authMethods.idp_name}
            primary={true}
          />
          {!authMethods.allow_google_sso && !authMethods.allow_microsoft_sso && !authMethods.allow_password_login && !authMethods.allow_saml_login && (
            <p className="text-xs text-gray-500 text-center">
              Your organization requires OIDC SSO for authentication
            </p>
          )}
        </div>
      )}

      {/* SAML SSO - Primary method when available and OIDC is not */}
      {authMethods.saml_available && !authMethods.oidc_available && (
        <div className="space-y-3">
          <SAMLSSOButton
            email={formData.email}
            onError={(err) => setError(err)}
            idpName={authMethods.idp_name}
            primary={true}
          />
          {!authMethods.allow_google_sso && !authMethods.allow_microsoft_sso && !authMethods.allow_password_login && (
            <p className="text-xs text-gray-500 text-center">
              Your organization requires SAML SSO for authentication
            </p>
          )}
        </div>
      )}

      {/* SAML SSO - Secondary method when OIDC is primary and SAML is allowed */}
      {authMethods.oidc_available && authMethods.allow_saml_login && authMethods.saml_available && (
        <SAMLSSOButton
          email={formData.email}
          onError={(err) => setError(err)}
          idpName="SAML SSO"
          primary={false}
        />
      )}

      {/* Alternative methods divider */}
      {(authMethods.saml_available || authMethods.oidc_available) && (authMethods.allow_google_sso || authMethods.allow_microsoft_sso || authMethods.allow_password_login) && (
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">Or continue with</span>
          </div>
        </div>
      )}

      {/* Non-SSO divider */}
      {!authMethods.saml_available && !authMethods.oidc_available && (
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">Choose sign-in method</span>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {/* Google SSO */}
        {authMethods.allow_google_sso && (
          <GoogleSSOButton onError={(err) => setError(err)} />
        )}

        {/* Microsoft SSO */}
        {authMethods.allow_microsoft_sso && (
          <MicrosoftSSOButton onError={(err) => setError(err)} />
        )}

        {/* Password Login */}
        {authMethods.allow_password_login && (
          <form onSubmit={handleSubmit}>
            <div className="space-y-3">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter your password"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loading ? 'Signing in...' : 'Sign in with Password'}
              </button>
              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => startOtpFlow('login')}
                  disabled={otpLoading}
                  className="text-indigo-600 hover:text-indigo-500 disabled:opacity-50"
                >
                  {otpLoading ? 'Sending code...' : 'Email me a sign-in code'}
                </button>
                <button
                  type="button"
                  onClick={() => startOtpFlow('reset')}
                  disabled={otpLoading}
                  className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
                >
                  Forgot password?
                </button>
              </div>
            </div>
          </form>
        )}

        {/* Passkey option if allowed by organization and browser supports it */}
        {isPasskeySupported && authMethods.allow_passkey_login && (
          <button
            type="button"
            onClick={handlePasskeyLogin}
            disabled={passkeyLoading}
            className="group relative w-full flex items-center justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            {passkeyLoading ? 'Authenticating...' : 'Sign in with Passkey'}
          </button>
        )}
      </div>

      {/* No methods available message */}
      {!authMethods.saml_available && !authMethods.oidc_available && !authMethods.allow_google_sso && !authMethods.allow_microsoft_sso && !authMethods.allow_password_login && !authMethods.allow_passkey_login && (
        <div className="text-center text-gray-600">
          <p>No authentication methods available for this email.</p>
          <p className="text-sm mt-2">Please contact your administrator.</p>
        </div>
      )}
    </div>
  );

  // Render registration form (unchanged from original)
  const renderRegistrationForm = () => (
    <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="first_name" className="sr-only">First Name</label>
            <input
              id="first_name"
              name="first_name"
              type="text"
              required
              value={formData.first_name}
              onChange={handleChange}
              className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="First Name"
            />
          </div>
          <div>
            <label htmlFor="last_name" className="sr-only">Last Name</label>
            <input
              id="last_name"
              name="last_name"
              type="text"
              required
              value={formData.last_name}
              onChange={handleChange}
              className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Last Name"
            />
          </div>
        </div>

        <div>
          <label htmlFor="email" className="sr-only">Email address</label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={formData.email}
            onChange={handleChange}
            className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Email address"
          />
        </div>

        <div>
          <label htmlFor="organization_id" className="sr-only">Organization ID</label>
          <input
            id="organization_id"
            name="organization_id"
            type="text"
            required
            value={formData.organization_id}
            onChange={handleChange}
            className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Organization ID"
          />
        </div>

        <div>
          <label htmlFor="role" className="sr-only">Role</label>
          <select
            id="role"
            name="role"
            value={formData.role}
            onChange={handleChange}
            className="relative block w-full px-3 py-2 border border-gray-300 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="ea">{labels.ea}</option>
            <option value="executive">{labels.executive}</option>
            <option value="admin">Admin</option>
          </select>
        </div>

        <div>
          <label htmlFor="password" className="sr-only">Password</label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="new-password"
            required
            value={formData.password}
            onChange={handleChange}
            className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Password"
          />
        </div>
      </div>

      <div>
        <button
          type="submit"
          disabled={loading}
          className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
        >
          {loading ? 'Processing...' : 'Sign up'}
        </button>
      </div>

      <div className="text-center">
        <button
          type="button"
          onClick={() => setIsLogin(true)}
          className="text-indigo-600 hover:text-indigo-500 text-sm"
        >
          Already have an account? Sign in
        </button>
      </div>
    </form>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {isLogin ? 'Sign in to your account' : 'Create your account'}
          </h2>
          <div className="mt-4 flex justify-center">
            <img src="/veyage-logo.svg" alt="Veyage" className="h-24" />
          </div>
        </div>

        {isLogin ? (
          step === 'email' ? renderEmailStep() : (otpMode ? renderOtpStep() : renderAuthStep())
        ) : (
          renderRegistrationForm()
        )}
      </div>
    </div>
  );
};

export default Login;
